import React, { FC, FormEvent, useCallback, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import api from '../../api';

type Validity = 'ok' | 'inactive' | 'not_started' | 'expired' | 'exhausted';

export interface PreviewDraft {
  code: string | null;
  publicLabel: string;
  welcomeCredits: number;
  welcomeMessageGuidance: string | null;
  active: boolean;
  startsAt: string | null;
  expiresAt: string | null;
  maxRedemptions: number | null;
}

/** A saved code (by id) or the unsaved values of the create/edit form. */
export type PreviewTarget = { codeId: string } | { draft: PreviewDraft };

interface TranscriptEntry {
  role: 'ai' | 'user';
  content: string;
}

interface PreviewState {
  phase: 'awaiting_name' | 'onboarding';
  language: string;
  userName: string | null;
  assistantName: string | null;
  turns: number;
  transcript: TranscriptEntry[];
}

interface PreviewResponse {
  success: boolean;
  branch: 'known_name' | 'asked_name' | 'name_reply' | 'onboarding_reply';
  messages: string[];
  state: PreviewState;
  toolCalls: string[];
  canReply: boolean;
  maxTurns: number;
  campaign: {
    code: string;
    fullCode: string;
    publicLabel: string;
    welcomeCredits: number;
    welcomeMessageGuidance: string | null;
  };
  validity: Validity;
  saved: boolean;
}

export const PREVIEW_LANGUAGES: { code: string; name: string }[] = [
  { code: 'en', name: 'English' },
  { code: 'fr', name: 'French' },
  { code: 'de', name: 'German' },
  { code: 'es', name: 'Spanish' },
  { code: 'it', name: 'Italian' },
];

const VALIDITY_WARNINGS: Record<Exclude<Validity, 'ok'>, string> = {
  inactive:
    'This code is deactivated — a real user sending it today would not be attributed to the campaign.',
  not_started:
    'This code has not started yet — a real user sending it today would not be attributed to the campaign.',
  expired:
    'This code has expired — a real user sending it today would not be attributed to the campaign.',
  exhausted:
    'This code has reached its redemption cap — a real user sending it today would not be attributed to the campaign.',
};

const errorMessage = (err: unknown, fallback: string): string => {
  const axiosErr = err as { response?: { data?: { error?: string } } };
  return axiosErr?.response?.data?.error || fallback;
};

const targetKey = (target: PreviewTarget): string => JSON.stringify(target);

interface Props {
  target: PreviewTarget;
  idPrefix: string;
  /** Disables the run controls, e.g. while the enclosing form is invalid. */
  disabled?: boolean;
  disabledReason?: string;
}

/**
 * "Simulate greeting": runs the real first-contact flow for a campaign code
 * against a virtual WhatsApp user (nothing is provisioned, redeemed or
 * logged) and shows the assistant's replies as WhatsApp-style bubbles. The
 * tester supplies an optional profile name and a language; when there is no
 * usable name the assistant asks for one and the tester can answer, up to a
 * small turn cap.
 */
const PlatformStatsCampaignCodePreview: FC<Props> = ({
  target,
  idPrefix,
  disabled = false,
  disabledReason,
}) => {
  const { getAccessTokenSilently } = useAuth0();

  const [profileName, setProfileName] = useState('');
  const [language, setLanguage] = useState('en');
  const [reply, setReply] = useState('');
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<PreviewResponse | null>(null);
  const [ranWithKey, setRanWithKey] = useState<string | null>(null);

  const stale = result !== null && ranWithKey !== targetKey(target);

  const run = useCallback(
    async (message?: string) => {
      if (running) return;
      setRunning(true);
      setError(null);
      const continuing = message !== undefined && result !== null && !stale;
      try {
        const res = await api.post<PreviewResponse>(
          '/platform-stats/acquisition-codes/preview',
          {
            ...target,
            language,
            profileName: profileName.trim() || null,
            ...(continuing ? { message, state: result.state } : {}),
          },
          {
            headers: {
              Authorization: `Bearer ${await getAccessTokenSilently()}`,
            },
          },
        );
        setResult(res.data);
        setRanWithKey(targetKey(target));
        setReply('');
      } catch (err) {
        setError(errorMessage(err, 'Failed to simulate the greeting'));
      } finally {
        setRunning(false);
      }
    },
    [
      running,
      result,
      stale,
      target,
      language,
      profileName,
      getAccessTokenSilently,
    ],
  );

  const start = (e: FormEvent) => {
    e.preventDefault();
    run();
  };

  const sendReply = (e: FormEvent) => {
    e.preventDefault();
    if (!reply.trim()) return;
    run(reply.trim());
  };

  const reset = () => {
    setResult(null);
    setRanWithKey(null);
    setError(null);
    setReply('');
  };

  const canReply = result !== null && result.canReply && !stale;
  const turnsLeft = result ? result.maxTurns - result.state.turns : 0;

  return (
    <div className='greeting-preview' data-testid={`${idPrefix}-preview`}>
      <form className='preview-controls' onSubmit={start}>
        <div className='field'>
          <label htmlFor={`${idPrefix}-preview-name`}>
            WhatsApp profile name
          </label>
          <input
            id={`${idPrefix}-preview-name`}
            type='text'
            value={profileName}
            onChange={(e) => setProfileName(e.target.value)}
            placeholder='Blank = no name on the profile'
            autoComplete='off'
            maxLength={120}
          />
        </div>
        <div className='field'>
          <label htmlFor={`${idPrefix}-preview-language`}>Language</label>
          <select
            id={`${idPrefix}-preview-language`}
            value={language}
            onChange={(e) => setLanguage(e.target.value)}
          >
            {PREVIEW_LANGUAGES.map((l) => (
              <option key={l.code} value={l.code}>
                {l.name}
              </option>
            ))}
          </select>
        </div>
        <div className='preview-actions'>
          <button
            type='submit'
            className='primary-btn'
            disabled={disabled || running}
            title={disabled ? disabledReason : undefined}
          >
            {running && !result
              ? 'Simulating…'
              : result
                ? 'Run again'
                : 'Simulate greeting'}
          </button>
          {result && (
            <button
              type='button'
              className='ghost-btn'
              onClick={reset}
              disabled={running}
            >
              Clear
            </button>
          )}
        </div>
      </form>
      {disabled && disabledReason && (
        <span className='field-hint'>{disabledReason}</span>
      )}
      <span className='field-hint'>
        Uses the real secretary, so wording varies run to run. Nothing is saved
        and no user is created.
      </span>

      {error && (
        <div className='form-error' role='alert'>
          {error}
        </div>
      )}

      {result && (
        <div className='preview-result'>
          {result.validity !== 'ok' && (
            <div className='preview-warning' role='status'>
              {VALIDITY_WARNINGS[result.validity]}
            </div>
          )}
          {stale && (
            <div className='preview-warning' role='status'>
              The form has changed since this simulation — run again to see the
              updated greeting.
            </div>
          )}
          <div className='wa-chat' aria-label='Simulated WhatsApp conversation'>
            {result.state.transcript.map((entry, i) => (
              <div
                key={i}
                className={`wa-bubble ${entry.role === 'user' ? 'from-user' : 'from-assistant'}`}
                data-testid={`${idPrefix}-preview-bubble-${entry.role}`}
              >
                {entry.content}
              </div>
            ))}
            {running && <div className='wa-typing'>Secretary is typing…</div>}
          </div>
          {result.toolCalls.length > 0 && (
            <span className='field-hint preview-tools'>
              Tools used in the last reply: {result.toolCalls.join(', ')}
            </span>
          )}
          {canReply ? (
            <form className='preview-reply' onSubmit={sendReply}>
              <label htmlFor={`${idPrefix}-preview-reply`} className='sr-only'>
                Reply as the user
              </label>
              <input
                id={`${idPrefix}-preview-reply`}
                type='text'
                value={reply}
                onChange={(e) => setReply(e.target.value)}
                placeholder={
                  result.state.phase === 'awaiting_name'
                    ? 'Reply as the user, e.g. their name…'
                    : 'Reply as the user…'
                }
                maxLength={1000}
                disabled={running}
              />
              <button
                type='submit'
                className='primary-btn'
                disabled={running || !reply.trim()}
              >
                {running ? 'Sending…' : 'Send'}
              </button>
              <span className='field-hint'>
                {turnsLeft} {turnsLeft === 1 ? 'reply' : 'replies'} left in this
                simulation
              </span>
            </form>
          ) : (
            !stale && (
              <span className='field-hint'>
                Simulation limit reached ({result.maxTurns} turns) — run again
                to start over.
              </span>
            )
          )}
        </div>
      )}
    </div>
  );
};

export default PlatformStatsCampaignCodePreview;
